<template>
  <div class="w-full">
    <Table
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :initialValues="initialValues"
      :pageLength="pageLength"
      :isLoading="isLoading"
      :tableFooter="false"
    >
      <template #tableRow="{ request, index }">
        <tr
          class="ltr:font-sans text-xs border-b border-brand-gray-300 w-full relative"
          :class="`z${index > 0 ? `-0` : index - 1}`"
        >
          <td class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300">
            <div class="flex items-center gap-4">
              <span class="text-primary cursor-pointer">
                <i
                  :class="[request?.id === selectedDraft ? 'fa-minus-circle' : 'fa-plus-circle']"
                  class="fa"
                  @click.prevent="toggleIcons(request)"
                ></i>
              </span>
            </div>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request?.requestNumber }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ formattedDate(request?.requestDate) }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span v-html="checkSponsoredName(request)"></span>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span v-html="getServiceName(request)"></span>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span>
              {{ request?.action?.text }}
            </span>
          </td>
          <td class="px-6 py-2">
            <div
              class="flex items-center justify-center gap-2 relative"
              :class="[index > 0 ? z - 0 : z - `${index - 1}`]"
            >
              <button
                class="bg-primary size-6 rounded-md text-white cursor-pointer text-center"
                v-if="allowView(request)"
                @click.prevent="viewRequest(request)"
              >
                <i class="fa fa-eye" aria-hidden="true"></i>
              </button>
              <button
                class="border border-primary size-6 rounded-md text-primary cursor-pointer"
                v-if="allowView(request)"
                @click.prevent="toggleDropdown(request)"
              >
                <i class="fa fa-bars" aria-hidden="true"></i>
              </button>
              <button
                class="bg-primary size-6 rounded-md text-white cursor-pointer"
                @click.prevent="viewTicket(request)"
              >
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
              </button>
              <div
                class="absolute z-50 right-8 top-8 bg-white"
                v-if="openActionsPanel === request?.id"
              >
                <DropdownList
                  :listItems="actionsList"
                  multiList="true"
                  open="true"
                  :key="request?.id"
                  @selected="selectedList($event, request)"
                >
                  <template #list="{ item }">
                    <span v-if="item.label" class="text-xs font-normal text-nowrap leading-4">
                      {{ item.label }}
                    </span>
                  </template>
                </DropdownList>
              </div>
            </div>
          </td>
        </tr>
        <tr
          v-if="request?.id === selectedDraft"
          class="ltr:font-sans text-xs border-b border-brand-gray-300 w-full bg-white relative left-12 right-12"
        >
          <td class="relative left-8 py-4">
            <div class="flex items-center gap-3">
              {{ $t('shipmentStatus') }}:
              <span>
                {{ getShipmentStatusText(request) }}
              </span>
            </div>
          </td>

          <td class="relative left-8 py-4" v-if="request?.shipmentNumber">
            <div class="flex items-center gap-3">
              {{ $t('shipmentNumber') }}:
              <span>
                {{ request?.shipmentNumber }}
              </span>
            </div>
          </td>

          <td
            class="relative left-8 py-4"
            v-if="request?.lookupDeliveryCompany && request?.shipmentNumber"
          >
            <div class="flex items-center gap-3">
              {{ $t('deliveryCompany') }}:
              <span v-html="getDeliveryCompanyText(request)" />
            </div>
          </td>

          <td class="relative left-8 py-4" v-if="showRefundAmount(request)">
            <div class="flex items-center gap-3">
              {{ $t('refundedAmount') }}:
              <span>
                {{ request?.refundedFeesAmount }}
              </span>
            </div>
          </td>

          <td
            class="relative left-8 py-4"
            v-if="request?.action?.id === InvitationForVirtualMeeting"
          >
            <div class="flex items-center gap-3">
              {{ $t('notes') }}:
              <span>
                {{ request?.actionComment }}
              </span>
            </div>
          </td>

          <td
            class="relative left-8 py-4"
            v-if="request?.action?.id === InvitationForVirtualMeeting"
          >
            <div class="flex items-center gap-3">
              {{ $t('showVirtualMeetingDetails') }}:
              <button
                class="bg-primary size-6 rounded-md text-white cursor-pointer"
                @click.prevent="goToVirtualMeetingDetails(request)"
              >
                {{ $t('showVirtualMeetingDetails') }}
              </button>
            </div>
          </td>
        </tr>
      </template>
    </Table>
  </div>
</template>

<script setup>
import Table from '@/components/Table/index.vue'
import DropdownList from '@/components/SimpleForm/DropdownList.vue'
import { ref } from 'vue'
import lookups from '@/assets/json/clientLookups.json'
import apiHelperService from '@/services'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { notify } from 'notiwind'
import { useAuthStore } from '@/stores/auth'

const i18n = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const props = defineProps({
  tableData: {
    type: Array,
    default: () => []
  },
  initialValues: {
    type: Object,
    default: () => {}
  },
  modules: {
    type: Array,
    default: () => []
  },
  parentService: {
    type: Array,
    default: () => []
  },
  services: {
    type: Array,
    default: () => []
  },
  transaction: {
    type: Array,
    default: () => []
  },
  shipmentStatus: {
    type: Array,
    default: () => []
  },
  deliveryCompanies: {
    type: Array,
    default: () => []
  },
  pageLength: {
    type: Number,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const {
  ServiceTransactions: { printFamilyBook, icaRefund },
  empostShipmentStatus: { returnedToStock },
  requestActions: { approved, InvitationForVirtualMeeting }
} = lookups

const selectedDraft = ref({})
const openActionsPanel = ref()

const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('RequestNumber')
  },
  {
    value: 3,
    label: i18n.t('RequestDate')
  },
  {
    value: 4,
    label: i18n.t('serviceBeneficiaryname')
  },
  {
    value: 5,
    label: i18n.t('serviceType')
  },
  {
    value: 6,
    label: i18n.t('status')
  }
])

const actionsList = ref([
  {
    value: 0,
    label: i18n.t('downlaodAttachments')
  },
  {
    value: 1,
    label: i18n.t('printReceipt')
  },
  {
    value: 2,
    label: i18n.t('relatedRequests')
  },
  {
    value: 3,
    label: i18n.t('shareService')
  },
  {
    value: 4,
    label: i18n.t('generateRequestQrCode')
  }
])

const checkSponsoredName = (request) => {
  let benificiaryName = ''
  const { arabicFullName, englishFullName } = request
  if (arabicFullName && englishFullName) {
    benificiaryName = arabicFullName + '<br />' + englishFullName
  } else {
    benificiaryName = i18n.t('NA')
  }
  return benificiaryName
}

const getServiceName = (request) => {
  const {
    module: { id: moduleId } = {},
    moduleService: { id: moduleServiceId } = {},
    subModuleService: { id: subModuleServiceId } = {},
    moduleTransaction: { id: moduleTransactionId } = {}
  } = request || {}

  const moduleName = getLookupText(props.modules, moduleId)
  const parentServiceName = getLookupText(props.parentService, moduleServiceId)
  const serviceName = getLookupText(props.services, subModuleServiceId)
  const transactionName = getLookupText(props.transaction, moduleTransactionId)

  const fullServiceName = `${moduleName}${parentServiceName}-${serviceName}-${transactionName}`
  return fullServiceName
}

const getLookupText = (entity, id) => {
  if (!entity || !id) return ''
  const record = entity.find((record) => record.id === id)
  return record ? record.text : ''
}

const viewRequest = (request) => {
  if (!request) return
  const { jsonServiceConfigration, requestNumber, serviceTransactionId, isExceptionalRequestFlag } =
    request
  let viewRequestUrl
  const jsonObject = JSON.parse(jsonServiceConfigration)
  if (!jsonObject?.viewRequestUrl) return
  if (isExceptionalRequestFlag && jsonObject.secondaryViewRequestUrl) {
    viewRequestUrl = jsonObject.secondaryViewRequestUrl
  } else {
    viewRequestUrl = jsonObject.viewRequestUrl
  }
  const replacedUrl = viewRequestUrl.replace(':requestNumber', requestNumber)
  router.replace(replacedUrl)
  if (!replacedUrl) {
    notify(
      {
        group: 'error',
        title: i18n.t('error'),
        text: i18n.t('missingRoute', { id: serviceTransactionId })
      },
      6000
    )
  }
}

const toggleDropdown = (request) => {
  const { id } = request
  if (!id) return
  if (openActionsPanel.value === id) {
    openActionsPanel.value = null
  } else {
    openActionsPanel.value = id
  }
}

const toggleIcons = (draft) => {
  if (selectedDraft.value === draft.id) {
    selectedDraft.value = null
  } else {
    selectedDraft.value = draft.id
  }
}

const selectedList = (item, request) => {
  if (item.value === 0) {
    downloadAttachments(request)
  }
  if (item.value === 1) {
    downloadReceipt(request)
  }
  if (item.value === 2) {
    openRelatedRequest(request)
  }
  if (item.value === 3) {
    handleShareService(request)
  }
  if (item.value === 4) {
    generateRequestQrCode(request)
  }
}

const getShipmentStatusText = (request) => {
  if (!request && !props.shipmentStatus) return
  const { lastShipmentStatusId, familyBookEmirateId } = request
  const status = props.shipmentStatus.filter((status) => status.id == lastShipmentStatusId)
  if (!status) return
  if (status.id === returnedToStock) {
    const { preferedLanguageCode } = authStore.user
    const preferedLanguage = preferedLanguageCode == 2 ? 'EN' : 'AR'
    const adminRegionText = getAdminRegionText(familyBookEmirateId, preferedLanguage)
    const statusValue = `${status.text}-${adminRegionText}`
    return statusValue
  }
}

const getDeliveryCompanyText = (request) => {
  if (!request) return
  const { lookupDeliveryCompany } = request
  const deliveryCompany = props.shipmentStatus.filter(
    (company) => company.id == lookupDeliveryCompany
  )
  if (!deliveryCompany) return
  return deliveryCompany.text
}

const adminRegionTexts = {
  1: { ar: 'أبو ظبي', en: 'ABU DHABI' },
  3: { ar: 'الشارقة', en: 'SHARJAH' },
  4: { ar: 'عجمان', en: 'AJMAN' },
  5: { ar: 'أم القيوين', en: 'UMM AL QUWAIN' },
  6: { ar: 'رأس الخيمة', en: 'RAS AL KHAIMAH' },
  7: { ar: 'الفجيرة', en: 'FUJAIRAH' },
  8: { ar: 'العين', en: 'AL AIN' },
  9: { ar: 'الظفرة', en: 'Al Dhafra' },
  27: { ar: 'دبي', en: 'DUBAI' }
}

const getAdminRegionText = (adminRegionId, languageId) => {
  if (!adminRegionId || !['ar', 'en'].includes(languageId)) {
    return ''
  }

  const region = adminRegionTexts[adminRegionId]
  return region ? region[languageId] : ''
}

const downloadAttachments = async (request) => {
  const { id, requestNumber } = request
  if (!request && !id && !requestNumber) return
  try {
    const params = {
      requestId: id,
      requestNumber: requestNumber
    }
    const result = await apiHelperService.get('/api/attachment/attachmentsFiles', { params })
    // TODO: Api error here
  } catch (error) {
    console.error('downloadAttachments', error)
  }
}

const downloadReceipt = async (request) => {
  const { requestNumber } = request
  if (!request && !requestNumber) return
  try {
    const params = {
      requestNumber: requestNumber
    }
    const result = await apiHelperService.get('/api/printReport/GetApplicationPayment', { params })
    // TODO: Api error here
  } catch (error) {
    console.error('downloadReceipt', error)
  }
}

const openRelatedRequest = async (request) => {
  // TODO: /relatedRequests route not available
}

const handleShareService = async (request) => {
  // TODO: /serviceQrCodeHandler route not available
}

const generateRequestQrCode = (request) => {
  // TODO: /requestQrCodeHandler route not available
}

const viewTicket = (request) => {
  // TODO: Report an issue route not available
}

const allowView = (request) => {
  const { serviceTransactionId, jsonServiceConfigration } = request
  if (serviceTransactionId === printFamilyBook) {
    return false
  }
  if (jsonServiceConfigration) {
    const jsonObject = JSON.parse(jsonServiceConfigration)
    return !!jsonObject?.viewRequestUrl
  }
}

const showRefundAmount = (request) => {
  const {
    refundedFeesAmount,
    serviceTransactionId,
    action: { id }
  } = request
  const isIcaRefund = serviceTransactionId === icaRefund
  const isApproved = id === approved
  return refundedFeesAmount && isIcaRefund && isApproved
}

const goToVirtualMeetingDetails = (request) => {
  // TODO: Data not available s
}

const formattedDate = (date) => {
  if (!date) return
  return DateTime.fromISO(date).toFormat('dd/MM/yyyy HH:mm')
}
</script>

<style></style>
